/**
 * Comprehensive Feature Guards Based on Subscription Plans
 * Automatically generated from subscription plan details
 *
 * FREE: Basic access to the platform
 * NOBILITY: Enhanced features and content access
 * ROYALTY: Professional features and priority support
 * IMPERIAL: Complete access to all features and premium support
 */

import { hasPaidSubscriptionAccess } from '@/lib/subscription-access';

export enum SubscriptionTier {
  FREE = 'free',
  NOBILITY = 'nobility',
  ROYALTY = 'royalty',
  IMPERIAL = 'imperial'
}

export enum FeatureAccess {
  // FREE Features (Everyone - No restrictions)
  PURCHASE_CURRICULUM = 'purchaseCurriculum',
  SEARCH_PROFESSIONALS = 'searchProfessionals',
  TAKE_CERTIFICATION = 'takeCertification',

  // NOBILITY Features (Nobility, Royalty, Imperial)
  BE_BOOKED_PROFESSIONAL = 'beBookedProfessional',
  SELL_CURRICULUM = 'sellCurriculum',

  // ROYALTY Features (Royalty, Imperial)
  CONTACT_BOOK_PROFESSIONALS = 'contactBookProfessionals',

  // IMPERIAL Features (Imperial only)
  PREMIUM_SELLER_FEATURES = 'premiumSellerFeatures',

  // Additional features that may need guarding
  VIEW_PUBLIC_CONTENT = 'viewPublicContent',
  VIEW_COURSES = 'viewCourses',
  VIEW_INSTRUCTORS = 'viewInstructors',
  VIEW_CURRICULUM = 'viewCurriculum',
  CREATE_ACCOUNT = 'createAccount',
  BASIC_PROFILE = 'basicProfile',
  COMMUNITY_FORUM = 'communityForum'
}

// Define features for each tier separately to avoid circular dependencies
const FREE_FEATURES = [
  // FREE features - No restrictions (available to all)
  FeatureAccess.PURCHASE_CURRICULUM,
  FeatureAccess.SEARCH_PROFESSIONALS,
  FeatureAccess.TAKE_CERTIFICATION,
  FeatureAccess.VIEW_PUBLIC_CONTENT,
  FeatureAccess.VIEW_COURSES,
  FeatureAccess.VIEW_INSTRUCTORS,
  FeatureAccess.VIEW_CURRICULUM,
  FeatureAccess.CREATE_ACCOUNT,
  FeatureAccess.BASIC_PROFILE,
  FeatureAccess.COMMUNITY_FORUM
];

const NOBILITY_FEATURES = [
  ...FREE_FEATURES,
  FeatureAccess.BE_BOOKED_PROFESSIONAL,  // "Be Booked as a Dance Professional"
  FeatureAccess.SELL_CURRICULUM          // "Sell Curriculum"
];

const ROYALTY_FEATURES = [
  ...NOBILITY_FEATURES,
  FeatureAccess.CONTACT_BOOK_PROFESSIONALS  // "Contact and Book Dance Professionals"
];

const IMPERIAL_FEATURES = [
  ...ROYALTY_FEATURES,
  FeatureAccess.PREMIUM_SELLER_FEATURES  // "Be Featured as a Premium Seller"
];

// Feature mapping based on subscription page table
export const SUBSCRIPTION_FEATURES = {
  [SubscriptionTier.FREE]: FREE_FEATURES,
  [SubscriptionTier.NOBILITY]: NOBILITY_FEATURES,
  [SubscriptionTier.ROYALTY]: ROYALTY_FEATURES,
  [SubscriptionTier.IMPERIAL]: IMPERIAL_FEATURES
};

/**
 * System admin features that are NOT available to any subscription tier
 * These require actual admin role, not just Imperial subscription
 */
export const SYSTEM_ADMIN_FEATURES = [
  'userManagement',
  'roleManagement',
  'systemSettings',
  'platformAnalytics',
  'allUsersAccess',
  'systemLogs',
  'securitySettings',
  'billingOversight',
  'platformConfiguration'
];

/**
 * Check if user has access to a specific feature based on their subscription tier
 */
export function hasFeatureAccess(
  userTier: string | undefined,
  feature: FeatureAccess,
  options?: {
    subscriptionStatus?: string | null;
    cancelAtPeriodEnd?: boolean | null;
  }
): boolean {
  if (!userTier) return false;

  const requiredTier = getRequiredTier(feature);
  const requiresPaidSubscription =
    requiredTier !== null && requiredTier !== SubscriptionTier.FREE;
  if (
    requiresPaidSubscription &&
    !hasPaidSubscriptionAccess({
      tier: userTier,
      status: options?.subscriptionStatus,
      cancelAtPeriodEnd: options?.cancelAtPeriodEnd,
    })
  ) {
    return false;
  }

  const normalizedTier = userTier.toLowerCase();

  // Map tier to enum value
  let mappedTier: SubscriptionTier;
  if (normalizedTier === 'royalty' || normalizedTier === 'royal') {  // Handle legacy ROYAL naming
    mappedTier = SubscriptionTier.ROYALTY;
  } else if (normalizedTier === 'free') {
    mappedTier = SubscriptionTier.FREE;
  } else if (normalizedTier === 'nobility') {
    mappedTier = SubscriptionTier.NOBILITY;
  } else if (normalizedTier === 'imperial') {
    mappedTier = SubscriptionTier.IMPERIAL;
  } else {
    return false; // Unknown tier
  }

  // Check if the mapped tier exists in features
  if (!SUBSCRIPTION_FEATURES[mappedTier]) {
    return false;
  }

  return SUBSCRIPTION_FEATURES[mappedTier].includes(feature);
}

/**
 * Check if user has system admin access (requires admin role, not subscription)
 */
export function hasSystemAdminAccess(
  userRoles: string[] | undefined,
  feature: string
): boolean {
  if (!userRoles || !Array.isArray(userRoles)) return false;

  const isSystemAdmin = userRoles.some(role =>
    role.toUpperCase() === 'ADMIN' || role.toUpperCase() === 'SYSTEM_ADMIN'
  );

  if (!isSystemAdmin) return false;

  return SYSTEM_ADMIN_FEATURES.includes(feature);
}

/**
 * Get all features available to a subscription tier
 */
export function getTierFeatures(tier: string): FeatureAccess[] {
  const normalizedTier = tier.toLowerCase();

  // Map tier to enum value
  let mappedTier: SubscriptionTier;
  if (normalizedTier === 'royalty' || normalizedTier === 'royal') {  // Handle legacy ROYAL naming
    mappedTier = SubscriptionTier.ROYALTY;
  } else if (normalizedTier === 'free') {
    mappedTier = SubscriptionTier.FREE;
  } else if (normalizedTier === 'nobility') {
    mappedTier = SubscriptionTier.NOBILITY;
  } else if (normalizedTier === 'imperial') {
    mappedTier = SubscriptionTier.IMPERIAL;
  } else {
    return []; // Unknown tier
  }

  return SUBSCRIPTION_FEATURES[mappedTier] || [];
}

/**
 * Get the minimum required tier for a specific feature
 */
export function getRequiredTier(feature: FeatureAccess): SubscriptionTier | null {
  for (const [tier, features] of Object.entries(SUBSCRIPTION_FEATURES)) {
    if (features.includes(feature)) {
      return tier as SubscriptionTier;
    }
  }
  return null;
}

/**
 * Check if a user can upgrade to access a specific feature
 */
export function canUpgradeForFeature(
  currentTier: string | undefined,
  feature: FeatureAccess
): boolean {
  if (!currentTier) return true; // Can always upgrade from no tier

  const requiredTier = getRequiredTier(feature);
  if (!requiredTier) return false; // Feature doesn't exist

  const tierHierarchy = {
    [SubscriptionTier.FREE]: 1,
    [SubscriptionTier.NOBILITY]: 2,
    [SubscriptionTier.ROYALTY]: 3,
    [SubscriptionTier.IMPERIAL]: 4
  };

  // Handle ROYAL as legacy naming
  const normalizedCurrentTier = currentTier?.toLowerCase() === 'royal' ? 'royalty' : currentTier?.toLowerCase();
  const currentLevel = tierHierarchy[normalizedCurrentTier as SubscriptionTier] || 0;
  const requiredLevel = tierHierarchy[requiredTier] || 0;

  return currentLevel < requiredLevel;
}

/**
 * Get upgrade suggestion for a feature
 */
export function getUpgradeSuggestion(
  currentTier: string | undefined,
  feature: FeatureAccess
): { tier: SubscriptionTier; reason: string } | null {
  if (!canUpgradeForFeature(currentTier, feature)) {
    return null;
  }

  const requiredTier = getRequiredTier(feature);
  if (!requiredTier) return null;

  const featureDescriptions = {
    [FeatureAccess.SELL_CURRICULUM]: 'Sell classes or products',
    [FeatureAccess.BE_BOOKED_PROFESSIONAL]: 'Get booked by students',
    [FeatureAccess.CONTACT_BOOK_PROFESSIONALS]: 'Contact and book dance professionals',
    [FeatureAccess.PREMIUM_SELLER_FEATURES]: 'Be featured as a premium seller'
  };

  // Use the required tier directly (already ROYALTY)
  const displayTier = requiredTier;

  return {
    tier: displayTier,
    reason: featureDescriptions[feature] || `Access to ${feature.replace(/([A-Z])/g, ' $1').toLowerCase()}`
  };
}

/**
 * Feature guard hook for React components
 */
export function useFeatureGuard() {
  return {
    hasFeatureAccess,
    hasSystemAdminAccess,
    getTierFeatures,
    getRequiredTier,
    canUpgradeForFeature,
    getUpgradeSuggestion
  };
}

/**
 * Higher-order component for feature protection
 * TODO: Move to a .tsx file if JSX is needed
 */
export function withFeatureGuard<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredFeature: FeatureAccess,
  fallbackComponent?: React.ComponentType<any>
) {
  return function FeatureGuardedComponent(props: P) {
    // This would need to be implemented with user context
    // For now, return the wrapped component
    return WrappedComponent;
  };
}
